import {useQuery} from '@apollo/client';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import gql from 'graphql-tag';
import {truncate} from 'lodash';
import moment from 'moment';
import React, {useMemo, useState} from 'react';
import ItemTable from '../../components/ItemTable';
import useMutationFHG from '../../fhg/components/data/useMutationFHG';
import DataLoadingAndErrors from '../../fhg/components/DataLoadingAndErrors';
import FHGTypography from '../../fhg/components/Typography';
import Grid from '../../fhg/components/Grid';
import ModalDialog from '../../fhg/components/dialog/ModalDialog';
import BugReportDetail from './BugReportDetail';

// Status -> color, shared between the table Chip and the detail dialog badge.
export const BUG_REPORT_STATUS_COLORS = {
   Submitted: '#6E6E6E',
   InReview: '#1E528F',
   NeedsClarification: '#E0A800',
   Resolved: '#0E8D0A',
   Closed: '#6E6E6E',
};

export const BUG_REPORT_STATUS_LABELS = {
   Submitted: 'Submitted',
   InReview: 'In Review',
   NeedsClarification: 'Needs Clarification',
   Resolved: 'Resolved',
   Closed: 'Closed',
};

export const BUG_REPORT_STATUSES = Object.keys(BUG_REPORT_STATUS_LABELS);

const BUG_REPORT_COMMENT_INFO = gql`
   fragment bugReportCommentInfo on BugReportComment {
      id
      message
      isFromAdmin
      createdDateTime
      createdByUser {
         id
         name
         email
      }
   }
`;

const BUG_REPORT_ATTACHMENT_INFO = gql`
   fragment bugReportAttachmentInfo on BugReportAttachment {
      id
      attachmentType
      originalFileName
      contentType
      fileSizeBytes
      createdDateTime
      url
   }
`;

const BUG_REPORT_INFO = gql`
   ${BUG_REPORT_COMMENT_INFO}
   ${BUG_REPORT_ATTACHMENT_INFO}
   fragment bugReportInfo on BugReport {
      id
      description
      status
      operatorId
      appVersion
      platform
      deviceModel
      testId
      testUUID
      createdDateTime
      createdByUser {
         id
         name
         email
      }
      operator {
         id
         name
      }
      attachments {
         ...bugReportAttachmentInfo
      }
      comments {
         ...bugReportCommentInfo
      }
   }
`;

export const BUG_REPORTS_ALL_WHERE_QUERY = gql`
   ${BUG_REPORT_INFO}
   query getBugReportsAllWhere {
      bugReports: bugReports_AllWhere(bugReportSearch: {}) {
         ...bugReportInfo
      }
   }
`;

// Lightweight query (just ids) backing the "Submitted" count badge in
// WebAppBar - kept separate from BUG_REPORTS_ALL_WHERE_QUERY so the nav bar,
// which is mounted on every super-admin page, doesn't pull the full
// description/attachments/comments payload just to show a number.
export const BUG_REPORTS_SUBMITTED_COUNT_QUERY = gql`
   query getBugReportsSubmittedCount {
      bugReports: bugReports_AllWhere(bugReportSearch: {status: ["Submitted"]}) {
         id
      }
   }
`;

const BUG_REPORT_UPDATE_STATUS = gql`
   ${BUG_REPORT_INFO}
   mutation bugReportUpdateStatus($bugReportId: Int!, $status: String!) {
      bugReport_UpdateStatus(bugReportId: $bugReportId, status: $status) {
         ...bugReportInfo
      }
   }
`;

const BUG_REPORT_DELETE = gql`
   mutation bugReportDelete($bugReportId: Int!) {
      bugReport_Delete(bugReportId: $bugReportId)
   }
`;

const BUG_REPORT_COMMENT_CREATE = gql`
   mutation bugReportCommentCreate($bugReportId: Int!, $message: String!) {
      bugReportComment_Create(bugReportId: $bugReportId, message: $message) {
         id
         message
         isFromAdmin
         createdDateTime
         createdByUser {
            id
            name
            email
         }
      }
   }
`;

/**
 * Super admin list of bug reports submitted from the mobile app. Selecting a row opens BugReportDetail, which owns
 * the status-change and reply UI; the actual mutations live here so a single refetch keeps both the table and the
 * open detail dialog (derived from the same query data) in sync.
 */
export default function BugReports() {
   const {data, loading, error} = useQuery(BUG_REPORTS_ALL_WHERE_QUERY);
   const [selectedId, setSelectedId] = useState();
   const [pendingDeleteId, setPendingDeleteId] = useState();

   const bugReports = data?.bugReports || [];
   const selectedBugReport = bugReports.find(bugReport => bugReport.id === selectedId);
   const pendingDeleteBugReport = bugReports.find(bugReport => bugReport.id === pendingDeleteId);

   const [updateStatus, {loading: isUpdatingStatus}] = useMutationFHG(BUG_REPORT_UPDATE_STATUS, {
      refetchQueries: () => ([{query: BUG_REPORTS_ALL_WHERE_QUERY}, {query: BUG_REPORTS_SUBMITTED_COUNT_QUERY}]),
   });
   const [postComment, {loading: isPostingComment}] = useMutationFHG(BUG_REPORT_COMMENT_CREATE, {
      refetchQueries: () => ([{query: BUG_REPORTS_ALL_WHERE_QUERY}, {query: BUG_REPORTS_SUBMITTED_COUNT_QUERY}]),
   });
   const [deleteBugReport, {loading: isDeleting}] = useMutationFHG(BUG_REPORT_DELETE, {
      refetchQueries: () => ([{query: BUG_REPORTS_ALL_WHERE_QUERY}, {query: BUG_REPORTS_SUBMITTED_COUNT_QUERY}]),
   });

   /**
    * Change the status of the selected bug report.
    */
   const handleChangeStatus = (status) => {
      updateStatus({variables: {bugReportId: selectedBugReport.id, status}});
   };

   /**
    * Post an admin reply to the selected bug report.
    */
   const handlePostComment = (message) => {
      postComment({variables: {bugReportId: selectedBugReport.id, message}});
   };

   /**
    * Open the delete confirmation for a row, without selecting/opening its detail dialog.
    */
   const handleDeleteClick = (event, bugReport) => {
      event.stopPropagation();
      setPendingDeleteId(bugReport.id);
   };

   /**
    * Delete the report pending confirmation. Also closes the detail dialog if the deleted
    * report happened to be the one currently open.
    */
   const handleDeleteConfirm = async () => {
      const bugReportId = pendingDeleteId;
      await deleteBugReport({variables: {bugReportId}});
      setPendingDeleteId(undefined);
      setSelectedId((currentId) => (currentId === bugReportId ? undefined : currentId));
   };

   const columns = useMemo(
      () => [
         {
            Header: <FHGTypography id={'bugReports.reporter.column'}/>,
            accessor: 'createdByUser.name',
            Cell: ({row}) => row.original.createdByUser?.name || row.original.createdByUser?.email || '',
         }, {
            Header: <FHGTypography id={'bugReports.operator.column'}/>,
            accessor: 'operator.name',
            Cell: ({row}) => row.original.operator?.name || '',
         }, {
            Header: <FHGTypography id={'bugReports.description.column'}/>,
            accessor: 'description',
            Cell: ({value}) => truncate(value, {length: 90}),
         }, {
            Header: <FHGTypography id={'bugReports.status.column'}/>,
            accessor: 'status',
            Cell: ({value}) => (
               <Chip
                  label={BUG_REPORT_STATUS_LABELS[value] || value}
                  size={'small'}
                  style={{backgroundColor: BUG_REPORT_STATUS_COLORS[value], color: 'white'}}
               />
            ),
         }, {
            Header: <FHGTypography id={'bugReports.submitted.column'}/>,
            accessor: 'createdDateTime',
            // Backend returns createdDateTime as a millisecond-timestamp string
            // (e.g. "1787178132847"), not an ISO date string - moment('x') is
            // the format token for "parse as a Unix ms timestamp", the same
            // pattern already used for lastTestDateTime/lastLoginDateTime in
            // admin/Users.js.
            Cell: ({value}) => moment(value, 'x').format('M/D/YYYY h:mm A'),
         }, {
            Header: '',
            id: 'actions',
            accessor: 'id',
            Cell: ({row}) => (
               <Button color={'error'} onClick={(event) => handleDeleteClick(event, row.original)}>
                  <FHGTypography noWrap id={'bugReports.delete.button'}/>
               </Button>
            ),
         },
      ], []
   );

   return (
      <Grid name={'Bug Reports Root Grid'} container direction={'column'} wrap={'nowrap'} style={{position: 'relative'}}>
         <DataLoadingAndErrors error={error} isLoading={loading}/>
         <Grid name={'Bug Reports Content Grid'} item resizable fullHeight fullWidth style={{width: '100%'}}>
            <Paper square style={{margin: 16, padding: 16, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
               <ItemTable titleKey={'bugReports.title'} columns={columns} items={bugReports}
                  onSelect={(bugReport) => setSelectedId(bugReport.id)} selectId={selectedId}/>
            </Paper>
         </Grid>
         {selectedBugReport && (
            <BugReportDetail
               bugReport={selectedBugReport}
               isSaving={isUpdatingStatus || isPostingComment}
               onChangeStatus={handleChangeStatus}
               onPostComment={handlePostComment}
               onClose={() => setSelectedId(undefined)}
            />
         )}
         {pendingDeleteBugReport && (
            <ModalDialog
               open={true}
               titleKey={'bugReports.delete.title'}
               messageKey={'bugReports.delete.message'}
               submitKey={'delete.button'}
               submitColor={'error'}
               onSubmit={handleDeleteConfirm}
               onClose={() => setPendingDeleteId(undefined)}
               isSaving={isDeleting}
               isEnabled={!isDeleting}
               maxWidth={'sm'}
               fullWidth
            />
         )}
      </Grid>
   );
}
